import React, { useState } from 'react'
import { Alert, Card, CardContent, Chip, Divider, FormControlLabel, Switch, TextField } from '@mui/material'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'

function normalizeProviderIds(providerIds) {
  return [...new Set((providerIds || []).map(Number).filter(Number.isInteger))]
}

function ProviderSharingStep({ items, setItems, providers, loading }) {
  const [searchTerm, setSearchTerm] = useState('')

  const toggleProvider = (itemKey, providerId, enabled) => {
    setItems((currentItems) =>
      currentItems.map((item) => {
        if (item.id !== itemKey) return item

        const currentProviderIds = normalizeProviderIds(item.provider_ids)
        const nextProviderIds = enabled
          ? [...new Set([...currentProviderIds, providerId])]
          : currentProviderIds.filter((id) => id !== providerId)

        return { ...item, provider_ids: nextProviderIds }
      }),
    )
  }

  if (items.length === 0) {
    return <Alert severity="info">Add at least one tank or asset before configuring sharing.</Alert>
  }

  if (!loading && providers.length === 0) {
    return (
      <Alert severity="warning">
        Your provider account has no direct or MANAGE ACL provider records available for sharing.
      </Alert>
    )
  }

  return (
    <MDBox mt={2}>
      <MDTypography variant="h5" fontWeight="medium" mb={1}>
        Provider sharing
      </MDTypography>
      <MDTypography variant="body2" color="text" mb={3}>
        Choose which providers can access each tank or asset. Sharing is off until enabled.
      </MDTypography>

      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder="Search providers..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 3 }}
      />

      {items.map((item, itemIndex) => {
        const selectedProviderIds = normalizeProviderIds(item.provider_ids)

        return (
          <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <MDTypography variant="subtitle2" fontWeight="bold">
                  {item.name || `Tank / Asset #${itemIndex + 1}`}
                </MDTypography>
                <Chip
                  size="small"
                  color={selectedProviderIds.length ? 'info' : 'default'}
                  label={`${selectedProviderIds.length} shared`}
                />
              </MDBox>

              <Divider sx={{ mb: 1 }} />

              <MDBox
                maxHeight={400}
                sx={{ overflowY: 'auto', pr: 4 }}
              >
                {providers
                  .filter((p) =>
                    p.provider_name.toLowerCase().includes(searchTerm.toLowerCase()),
                  )
                  .map((provider) => {
                    const providerId = Number(provider.provider_id)
                    const checked = selectedProviderIds.includes(providerId)

                    return (
                      <MDBox
                        key={provider.provider_id}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        py={0.75}
                        gap={2}
                      >
                        <MDBox minWidth={0}>
                          <MDTypography variant="button" fontWeight="medium" display="block">
                            {provider.provider_name}
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            {provider.relationship === 'DIRECT'
                              ? 'Your provider organization'
                              : `Provider ACL: ${provider.acl_permission}`}
                          </MDTypography>
                        </MDBox>

                        <FormControlLabel
                          label={checked ? 'Shared' : 'Not shared'}
                          labelPlacement="start"
                          control={
                            <Switch
                              checked={checked}
                              disabled={loading || provider.can_share !== true}
                              onChange={(event) =>
                                toggleProvider(item.id, providerId, event.target.checked)
                              }
                              color="primary"
                              inputProps={{
                                'aria-label': `Share ${item.name || 'item'} with ${provider.provider_name}`,
                              }}
                            />
                          }
                        />
                      </MDBox>
                    )
                  })}
                </MDBox>
            </CardContent>
          </Card>
        )
      })}
    </MDBox>
  )
}

export default ProviderSharingStep
